import { ofetch, Headers } from 'ofetch';
import {
  organizationDomainHeaderName,
  organizationSlugHeaderName,
  useApi,
} from '~/api';
import { resolveApiBaseUrl } from '~/api/base-url';
import {
  createOrganizationCache,
  OrganizationLookup,
} from '~/api/organization-cache';
import { components, operations } from '~~/api-types';
import { useRuntimeConfig } from '#imports';

export type OrganizationDto =
  components['schemas']['OrganizationAppResponseDto'];

export type MenuItemDto = components['schemas']['MenuItemResponseDto'];

export type SupportFundsSettingsDto =
  components['schemas']['SupportFundsSettingsResponseDto'];

export type SupportFundsBusinessDto =
  components['schemas']['SupportFundsBusinessResponseDto'];

export const apiGetCurrentOrganization = (data: {
  organizationSlug?: string;
  organizationDomain?: string | null;
}): Promise<OrganizationDto> => {
  const config = useRuntimeConfig();

  const headers = new Headers();

  if (data.organizationSlug !== undefined) {
    headers.append(organizationSlugHeaderName, data.organizationSlug);
  }
  if (data.organizationDomain) {
    headers.append(organizationDomainHeaderName, data.organizationDomain);
  }

  return ofetch(`/app/organizations/current`, {
    retry: 0,
    baseURL: resolveApiBaseUrl(config, import.meta.server),
    headers,
  });
};

const serverOrganizationCache = import.meta.server
  ? createOrganizationCache<OrganizationDto>()
  : null;

export const getCurrentOrganizationOnServer = (
  lookup: OrganizationLookup,
): Promise<OrganizationDto | null> => {
  if (serverOrganizationCache === null) {
    throw new Error('getCurrentOrganizationOnServer runs on the server only');
  }
  const config = useRuntimeConfig();

  return serverOrganizationCache.read(resolveApiBaseUrl(config, true), lookup);
};

export type GetOrganizationsListQueryParams =
  operations['AppGetOrganizationsListEndpoint_handler']['parameters']['query'];
export type PaginatedOrganizationDto =
  components['schemas']['PaginatedAppOrganizationResponseDto'];

export const apiGetOrganizationList = (
  params?: GetOrganizationsListQueryParams,
): Promise<PaginatedOrganizationDto> => {
  const api = useApi();

  return api(`/app/organizations`, { params });
};

export type CookieConsentBannerDto =
  components['schemas']['CookieConsentBannerResponseDto'];

export const apiGetActiveCookieConsentBanner =
  async (): Promise<CookieConsentBannerDto | null> => {
    const config = useRuntimeConfig();
    const api = useApi();

    try {
      const data = await api(`/app/organizations/cookie-consent-banner/active`);
      return data;
    } catch {
      return null;
    }
  };

export const apiGetSupportFunds =
  async (): Promise<SupportFundsSettingsDto> => {
    const api = useApi();
    return await api(`/app/organizations/current/support-funds`);
  };

export const apiGetSupportFundsBusinesses = async (
  businessId?: number,
): Promise<SupportFundsBusinessDto[]> => {
  const api = useApi();
  return await api(`/app/organizations/current/support-funds/businesses`, {
    params: businessId ? { businessId } : undefined,
  });
};
